import { PostgrestError } from "@supabase/supabase-js";
import { type Emoji } from "components/EmojiPicker/EmojiPicker";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbEvent = Database["public"]["Tables"]["events"];
export interface EventRow extends Omit<DbEvent["Row"], "icon" | "location" | "weather"> {
  icon: Emoji;
  location: EventLocation | null;
  weather: Weather | null;
}
export interface EventInsert extends Omit<DbEvent["Insert"], "icon" | "location" | "weather"> {
  icon?: Emoji;
  location?: EventLocation | null;
  weather?: Weather | null;
}
export interface EventUpdate extends Omit<DbEvent["Update"], "icon" | "location" | "weather"> {
  icon?: Emoji;
  location?: EventLocation | null;
  weather?: Weather | null;
}

export type EventStatus = Database["public"]["Enums"]["event_status"];

export interface GetClosedEvents {
  icon: Emoji;
  id: number;
  name: string;
  start_date: string;
}

export interface EventLocation {
  coordinates: {
    lat: number;
    lng: number;
  };
  name: string;
}

export type WeatherMain = "Clear" | "Clouds" | "Drizzle" | "Rain" | "Snow" | "Thunderstorm" | "Unknown";

export interface WeatherInterval {
  dt: number;
  temp: number;
  humidity: number;
  precip: number;
  wind: number;
  main: WeatherMain;
}

export interface Weather {
  updated_at?: Date;
  data?: WeatherInterval[][];
}

class EventApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  async getEvent(
    eventId: EventRow["id"],
    status: Database["public"]["Enums"]["event_status"] = "open"
  ): Promise<{
    event: EventRow | null;
    error: PostgrestError | null;
  }> {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .eq("id", eventId)
      .eq("status", status)
      .maybeSingle();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load event.",
          severity: "error",
        });
      }

      logSentryError(error, "EVENT_API: getEvent");
      return { event: null, error };
    }

    return { event: data, error: null };
  }

  /**
   * Get all events
   *
   * @param groupId
   * @returns
   */
  async getAllEvents(groupIds: EventRow["id"][]): Promise<{ events: EventRow[]; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .in("group_id", groupIds)
      .eq("status", "open")
      .order("name");

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load events.",
          severity: "error",
        });
      }

      logSentryError(error, "EVENT_API: getAllEvents");
      return { events: [], error };
    }

    return { events: data as EventRow[], error: null };
  }

  /**
   * Get event by name
   *
   * @param groupId
   * @returns
   */
  async getEventTemplates(name: EventRow["name"], groupId: number): Promise<EventRow[]> {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .ilike("name", `%${name}%`)
      .eq("group_id", groupId)
      .eq("status", "closed")
      .order("name")
      .limit(20);

    if (error && parseInt(error.code) !== 20) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load event templates.",
          severity: "error",
        });
      }

      logSentryError(error, "EVENT_API: getEventTemplates");
      return [];
    }

    return (data as EventRow[]) ?? [];
  }

  /**
   * Get all events that belongs to a group
   *
   * @param groupId
   * @returns
   */
  async getEventsByGroup(groupId: EventRow["group_id"]): Promise<{ events: EventRow[]; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .eq("group_id", groupId)
      .eq("status", "open")
      .order("name");

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load events.",
          severity: "error",
        });
      }

      logSentryError(error, "EVENT_API: getEventsByGroup");
      return { events: [], error };
    }

    return { events: data as EventRow[], error: null };
  }

  async createEvent(eventData: {
    details: EventInsert["details"];
    end_date: EventInsert["end_date"];
    group_id: EventInsert["group_id"];
    icon: EventInsert["icon"];
    location: EventInsert["location"];
    name: EventInsert["name"];
    participants_limit: EventInsert["participants_limit"];
    start_date: EventInsert["start_date"];
    vote_before: EventInsert["vote_before"];
    weather: EventInsert["weather"];
  }): Promise<{ event: { id: EventRow["id"] }; error: null } | { event: null; error: PostgrestError }> {
    const { data: event, error } = await supabase
      .from("events")
      .insert(eventData as DbEvent["Insert"])
      .select("id")
      .single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to create event.",
          severity: "error",
        });
      }

      logSentryError(error, "EVENT_API: createEvent");
      return { event: null, error };
    }

    return { event, error: null };
  }

  async updateEvent(
    eventId: number,
    eventData: {
      details?: EventUpdate["details"];
      end_date?: EventUpdate["end_date"];
      icon?: EventUpdate["icon"];
      location?: EventUpdate["location"];
      name?: EventUpdate["name"];
      participants_limit?: EventUpdate["participants_limit"];
      start_date?: EventUpdate["start_date"];
      status?: EventUpdate["status"];
      vote_before?: EventUpdate["vote_before"];
      weather?: EventUpdate["weather"];
    }
  ): Promise<{ error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("events")
      .update(eventData as DbEvent["Update"])
      .eq("id", eventId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update event.",
          severity: "error",
        });
      }

      logSentryError(error, "EVENT_API: updateEvent");
      return { error };
    }

    if (data) {
      return { error: null };
    }

    return { error: null };
  }

  /**
   * Get all closed events that belongs to a group
   *
   * @param groupId
   * @returns
   */
  async getClosedEvents(groupId: number): Promise<{
    events: Array<
      { id: EventRow["id"] } & { name: EventRow["name"] } & {
        start_date: EventRow["start_date"];
      } & { icon: EventRow["icon"] }
    >;
    error: PostgrestError | null;
  }> {
    const { data, error } = await supabase
      .from("events")
      .select("id, name, start_date, icon")
      .eq("group_id", groupId)
      .eq("status", "closed")
      .order("start_date", { ascending: false });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load timeline events.",
          severity: "error",
        });
      }

      logSentryError(error, "EVENT_API: getClosedEvents");
      return { events: [], error };
    }

    return { events: data, error: null };
  }

  /**
   * Delete event
   *
   * @param eventId
   * @returns
   */
  async deleteEvent(eventId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.from("events").delete().eq("id", eventId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to delete event.",
          severity: "error",
        });
      }
      logSentryError(error, "EVENT_API: deleteEvent");
      return { error };
    }

    return { error: null };
  }

  /**
   * Get weather intervals
   *
   * @param coordinates
   * @returns
   */
  async updateWeather(eventId: number): Promise<{ data: WeatherInterval[][]; error: string | null }> {
    const { data, error } = await supabase.functions.invoke("update-weather", {
      body: JSON.stringify({
        eventId,
      }),
    });

    if (this.showNotification && error) {
      this.showNotification({
        message: "Unable to load weather data.",
        severity: "error",
      });
      logSentryError(error, "EVENT_API: updateWeather");
      return { data: [], error: "Unable to load weather data." };
    }

    if (!data) {
      throw new Error("'data' is not present");
    }

    return {
      data,
      error: null,
    };
  }
}

export default EventApi;
